import { Link, Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { MetricCard } from "@/components/ui/metric-card";
import { usePageMeta } from "@/hooks/usePageMeta";
import type { BootstrapResponse } from "@/lib/api";

type AuthStatus =
  | { kind: "loading" }
  | { kind: "anonymous"; env: string }
  | { kind: "authenticated"; bootstrap: BootstrapResponse };

const KPI_LABELS: Array<{ label: string; accent: string; copy: string }> = [
  {
    label: "Sessions",
    accent: "arriving",
    copy: "Sessions arriving — first report runs after you connect.",
  },
  {
    label: "Engaged sessions",
    accent: "engagement",
    copy: "Real engagement numbers land here once a property is selected.",
  },
  {
    label: "Key events",
    accent: "soon",
    copy: "Key events sync soon — connect a property to start.",
  },
];

export function AppHome({ auth }: { auth: AuthStatus }) {
  usePageMeta({ title: "Traffic Snapshot by Sked Social" });
  if (auth.kind === "loading") {
    return <AppShell eyebrow="Traffic report" title="Loading…" />;
  }

  // Authenticated users always land on the report — there's no separate
  // "welcome back" surface anymore. A user with no property gets bounced
  // to the property picker; a user with a property gets the report.
  if (auth.kind === "authenticated") {
    if (!auth.bootstrap.defaultProperty) {
      return <Navigate to="/app/properties" replace />;
    }
    return <Navigate to="/app/report" replace />;
  }

  return (
    <AppShell
      eyebrow="Traffic report"
      appEnv={auth.env}
      title={
        <>
          Connect GA4 to see your <em className="font-serif text-purple-700">social</em> traffic
        </>
      }
      subtitle="We'll ask Google for read-only analytics access, then show you a social-first report for the property you pick."
      actions={
        <Button asChild>
          <Link to="/app/connect">
            Sign in with Google
            <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
          </Link>
        </Button>
      }
    >
      <AnonymousBody />
    </AppShell>
  );
}

function AnonymousBody(): ReactNode {
  return (
    <div className="space-y-6">
      <Alert>
        <i className="fa-regular fa-shield-check text-sm" aria-hidden="true" />
        <AlertTitle>Privacy-minimal by default</AlertTitle>
        <AlertDescription>
          We don't store GA4 row data. Reports fetch live from Google and are cached for up to an
          hour in our data store.
        </AlertDescription>
      </Alert>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {KPI_LABELS.map((m) => (
          <MetricCard
            key={m.label}
            state="empty"
            label={m.label}
            emptyMessage={m.copy}
            emptyAccent={m.accent}
          />
        ))}
      </div>
    </div>
  );
}
